/**
 * 受信者のプライバシー保護（リモート画像の扱い）。
 *
 * **契約者面の API 4 本（`routes/portal/privacy.ts`）は、この画面が無いあいだ
 * 1 か所からも呼ばれていなかった。** 既定が最も守られる側（`proxy`）なので
 * 事故にはならないが、
 *   - 自社 CDN を bypass に登録して「画像が出ない」を自分で切り分ける
 *   - 上位プランで開放される出口 IP ローテ／プリフェッチを実際に使う
 * のどちらも契約者にはできず、上位プランを買っても差が体感できなかった。
 *
 * ## 何を守る機能なのか（画面に必ず書く）
 *
 * HTML メールの `<img src="https://tracker/pixel.gif?id=…">` は、受信者が
 * 本文を開いた瞬間に受信者の IP・UA・開封時刻を送信者へ渡す。ここを
 * 「こちらが代理で取得する」に倒すと、送信者に見えるのは我々のサーバーだけになる。
 * **設定の意味を書かずにスイッチだけ並べても誰も触らない**ので、
 * それぞれ何が漏れなくなるのかを添える。
 *
 * ## configured と effective を両方出す
 *
 * `GET /privacy/remote-images` が両方返すのは、プランで閉じている項目が
 * 「設定したのに効いていない」に見えないようにするため（API 側のコメント）。
 * だからこの画面は features が false の項目をロック表示にし、
 * 実効値を別に添える。
 */
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  Field,
  KeyValue,
  PageHeader,
  Section,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SwitchRow,
  formatBytes,
  formatCount,
  type DataTableColumn,
} from '@mail-sv/ui';
import {
  ORG_PERMISSION,
  PROXY_EGRESS_MODE,
  REMOTE_IMAGE_MODE,
  TRACKER_VERDICT_LABELS_JA,
  labelOf,
  type OrgPrivacyPolicy,
  type ProxyEgressMode,
  type RemoteImageMode,
} from '@mail-sv/shared';
import { EyeOff, ShieldCheck } from 'lucide-react';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { AppLink } from '../lib/nav.js';
import { Async } from '../components/async.js';
import { FormAlert, SubmitBar } from '../components/form.js';
import { HelpNote } from '../components/help.js';
import { usePermission } from '../stores/auth.js';
import type {
  CountedList,
  CustomHostname,
  ImageProxyHostStats,
  PrivacyPolicyView,
} from '../lib/api-types.js';

/** リモート画像の扱いの説明。`labelOf()` は短い名前しか返さないので別に持つ。 */
const MODE_HELP: Record<RemoteImageMode, string> = {
  proxy:
    'こちらのサーバーが代わりに画像を取りに行き、受信者へ渡します。送信者に伝わるのは当社のサーバーの情報だけで、受信者の IP アドレス・端末・開封時刻は伝わりません。',
  block:
    '外部の画像を一切読み込みません。最も安全ですが、正規のメールでも画像が表示されなくなります。',
  direct:
    '受信者のブラウザが送信者のサーバーへ直接取りに行きます。受信者の IP アドレス・端末・開封時刻が送信者に伝わります。',
};

/** キャッシュ保持の選択肢（秒）。長いほど開封回数が漏れないが更新も反映されない。 */
const CACHE_TTL_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 3600, label: '1 時間' },
  { value: 86_400, label: '1 日' },
  { value: 604_800, label: '7 日（推奨）' },
  { value: 2_592_000, label: '30 日' },
];

export function PrivacyPage() {
  const canWrite = usePermission(ORG_PERMISSION.write);
  const policy = useQuery({
    queryKey: qk.privacyPolicy(),
    queryFn: () => api.get<PrivacyPolicyView>('/privacy/remote-images'),
  });

  return (
    <>
      <PageHeader
        title="受信者のプライバシー保護"
        description="メール本文に埋め込まれた外部画像の扱いを設定します。既定では、開封の追跡を無効にする設定になっています。"
      />

      <div className="space-y-6">
        <HelpNote title="なぜこの設定があるのか">
          HTML メールに埋め込まれた画像は、受信者が本文を開いた瞬間に送信者のサーバーへ通信します。
          送信者はそれだけで受信者の IP アドレス・端末・開封の時刻と回数を知ることができます。
          既定では、当社のサーバーが代わりに画像を取得してお渡しするため、これらは送信者に伝わりません。
        </HelpNote>

        <WebmailHostnameNotice />

        <Async query={policy} lines={6}>
          {(data) => (
            <>
              <Section title="外部画像の取り扱い">
                <PrivacyForm data={data} canWrite={canWrite} />
              </Section>
              <Section
                title="どこから画像を取得しているか"
                description="当社が代理で取得した画像の取得元です。受信者を特定できる情報は記録していません。"
              >
                <HostStatsCard />
              </Section>
            </>
          )}
        </Async>
      </div>
    </>
  );
}

/**
 * **この設定がいつ効くのか**を先に言う。
 *
 * 本文の `<img>` を書き換えるのは Webメールの応答を通すときだけで、
 * 組織の解決は **Host ヘッダ**で行う（`services/webmail-message-proxy.ts` の
 * `resolveWebmailOrganizationId()` が `custom_hostnames` の
 * `kind='webmail' AND status='active'` を引く）。前段の Caddy も
 * 書き換えの handle をお客様専用ホスト名のサイトブロックに置いている
 * （`deploy/vps/site.caddy`）。
 *
 * つまり **共通の Webメール URL を使っている組織では、この画面の設定は
 * 保存はできても本文の書き換えが 1 度も走らない。** 黙って無保護になる
 * ほうが「設定したのに追跡されている」より悪いので、状態を出して
 * 設定画面（`/branding`）へ誘導する。
 *
 * ホスト名一覧は `domainRead` 権限が要る。読めないメンバー
 * （403）では**何も出さない**（押せない画面を案内しても意味が無い）。
 */
function WebmailHostnameNotice() {
  const hostnames = useQuery({
    queryKey: qk.customHostnames(),
    queryFn: () => api.get<CountedList<CustomHostname>>('/custom-hostnames'),
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (hostnames.isPending || hostnames.isError) return null;

  const webmail = hostnames.data.items.filter((h) => h.kind === 'webmail');
  const active = webmail.find((h) => h.status === 'active');
  if (active) {
    return (
      <Alert variant="success">
        <AlertTitle>この設定は有効です</AlertTitle>
        <AlertDescription>
          Webメールを <span className="font-mono">{active.hostname}</span>{' '}
          でご利用いただいているため、本文の外部画像はこの画面の設定どおりに処理されます。
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert variant="warning">
      <AlertTitle>お客様専用の Webメール URL を設定するまで、この保護は働きません</AlertTitle>
      <AlertDescription className="space-y-2">
        <p>
          本文の書き換えは、Webメールをお客様専用のホスト名（例:{' '}
          <span className="font-mono">webmail.example.jp</span>
          ）で開いたときにだけ行われます。共通の Webメール URL
          をお使いのあいだは、ここでの設定は保存されますが本文には反映されません
          {webmail.length > 0 ? '（ホスト名は登録済みですが、まだ有効になっていません）' : ''}。
        </p>
        <Button asChild variant="outline" size="sm">
          <AppLink to="/branding">Webメールのホスト名を設定する</AppLink>
        </Button>
      </AlertDescription>
    </Alert>
  );
}

function PrivacyForm({ data, canWrite }: { data: PrivacyPolicyView; canWrite: boolean }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = React.useState<OrgPrivacyPolicy>(() => toDraft(data));
  React.useEffect(() => {
    setDraft(toDraft(data));
  }, [data]);

  const save = useMutation({
    mutationFn: () => api.put<unknown>('/privacy/remote-images', draft),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.privacyPolicy() }),
  });

  const set = <K extends keyof OrgPrivacyPolicy>(key: K, value: OrgPrivacyPolicy[K]): void => {
    setDraft((d) => ({ ...d, [key]: value }));
  };

  const proxying = draft.remoteImageMode === 'proxy';
  /*
   * **プランで閉じているかどうかを 1 か所で判定する。** ここを各行に散らすと、
   * 「ロック表示は出ているのに入力できる」ような食い違いがすぐ生まれる。
   */
  const lockedProxy = !data.features.imageProxy;
  const lockedRotation = !data.features.imageProxyIpRotation;
  const lockedPrefetch = !data.features.imageProxyPrefetch;

  return (
    <div className="space-y-4">
      {lockedProxy ? (
        <Alert variant="info">
          <AlertTitle>ご利用中のプランでは代理取得を利用できません</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>
              「こちらで代理取得」を選んでも、実際には外部画像を読み込まない（ブロックする）動作になります。
              保護を弱めるのではなく、画像を出さない側に倒しています。
            </p>
            <Button asChild variant="outline" size="sm">
              <AppLink to="/billing/plan">プランを見る</AppLink>
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">設定</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-5"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate();
            }}
          >
            <FormAlert error={save.error} />
            {save.isSuccess ? (
              <Alert variant="success">
                <AlertDescription>保存しました。数分以内に反映されます。</AlertDescription>
              </Alert>
            ) : null}

            <Field label="外部画像の読み込み" hint={MODE_HELP[draft.remoteImageMode]}>
              <div className="flex flex-wrap gap-2">
                {REMOTE_IMAGE_MODE.map((m) => (
                  <Button
                    key={m}
                    type="button"
                    variant={draft.remoteImageMode === m ? 'default' : 'outline'}
                    size="sm"
                    disabled={!canWrite}
                    onClick={() => set('remoteImageMode', m)}
                  >
                    {m === 'block' ? '読み込まない' : labelOf(m)}
                  </Button>
                ))}
              </div>
            </Field>

            {/*
             * **実効値を必ず添える。** 設定値だけを描くと、プランで
             * `proxy` が閉じている契約者の画面に「代理取得」と出続ける。
             */}
            {draft.remoteImageMode !== data.effective.remoteImageMode ? (
              <Alert variant="warning">
                <AlertTitle>実際に動作している設定と異なります</AlertTitle>
                <AlertDescription>
                  現在の実効値は「
                  {data.effective.remoteImageMode === 'block'
                    ? '読み込まない'
                    : labelOf(data.effective.remoteImageMode)}
                  」です。プランの制限により、選んだ設定がそのままは適用されません。
                </AlertDescription>
              </Alert>
            ) : null}

            <div className="space-y-1">
              <SwitchRow
                label="開封追跡ピクセルを取り除く"
                description="1×1 の透明画像や既知の計測事業者からの画像は、取得しても中身を渡しません（表示上の見た目は変わりません）。"
                checked={draft.stripTrackingPixels}
                disabled={!canWrite || !proxying}
                onCheckedChange={(v) => set('stripTrackingPixels', v)}
              />
              <SwitchRow
                label="リンクのクリック時に参照元を送らない"
                description="本文中のリンクを開いたときに「どのメールから来たか」が相手に伝わらなくなります。"
                checked={draft.stripReferrer}
                disabled={!canWrite}
                onCheckedChange={(v) => set('stripReferrer', v)}
              />
              <SwitchRow
                label={
                  <span className="flex items-center gap-2">
                    受信時にあらかじめ取得する
                    {lockedPrefetch ? (
                      <Badge variant="muted" size="sm">
                        上位プラン
                      </Badge>
                    ) : null}
                  </span>
                }
                description="メールが届いた時点で画像を取得しておくため、送信者のログに残る時刻が「配送時刻」になり、開封した時刻そのものが伝わりません。"
                checked={draft.prefetchEnabled}
                disabled={!canWrite || !proxying || lockedPrefetch}
                onCheckedChange={(v) => set('prefetchEnabled', v)}
              />
            </div>

            <Field
              label={
                <span className="flex items-center gap-2">
                  取得元の IP アドレス
                  {lockedRotation ? (
                    <Badge variant="muted" size="sm">
                      上位プラン
                    </Badge>
                  ) : null}
                </span>
              }
              hint="毎回入れ替えると、送信者側で「同じ受信者が何回開いたか」を IP アドレスで束ねられなくなります。"
            >
              <Select
                value={draft.egressMode}
                onValueChange={(v) => set('egressMode', v as ProxyEgressMode)}
              >
                <SelectTrigger disabled={!canWrite || !proxying || lockedRotation}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROXY_EGRESS_MODE.map((m) => (
                    <SelectItem key={m} value={m}>
                      {labelOf(m)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field
              label="取得した画像を保持する期間"
              hint="長いほど再取得が減り、開封の回数が送信者に伝わりにくくなります。逆に、画像が差し替えられても反映が遅くなります。"
            >
              <Select
                value={String(draft.cacheTtlSeconds)}
                onValueChange={(v) => set('cacheTtlSeconds', Number(v))}
              >
                <SelectTrigger disabled={!canWrite || !proxying}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CACHE_TTL_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={String(o.value)}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field
              label="代理取得せずに直接読み込むホスト（1 行に 1 件）"
              hint="自社の CDN など、追跡の心配がない配信元をここに書くと、代理取得を通さず直接読み込みます。最大 50 件。"
            >
              <textarea
                rows={4}
                value={draft.bypassHosts.join('\n')}
                disabled={!canWrite || !proxying}
                onChange={(e) => set('bypassHosts', splitHosts(e.target.value))}
                className="flex w-full rounded-md border border-input bg-panel-elevated px-3 py-2 font-mono text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                placeholder={'cdn.example.com\nimages.example.co.jp'}
              />
            </Field>

            {canWrite ? <SubmitBar submitLabel="保存する" pending={save.isPending} /> : null}
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5">
          <KeyValue
            columns={3}
            items={[
              {
                key: '実際に効いている設定',
                value: (
                  <span className="flex items-center gap-1.5">
                    {data.effective.remoteImageMode === 'direct' ? (
                      <EyeOff className="size-3.5 text-warning" aria-hidden />
                    ) : (
                      <ShieldCheck className="size-3.5 text-success" aria-hidden />
                    )}
                    {data.effective.remoteImageMode === 'block'
                      ? '読み込まない'
                      : labelOf(data.effective.remoteImageMode)}
                  </span>
                ),
              },
              { key: '取得元 IP', value: labelOf(data.effective.egressMode) },
              {
                key: '最終更新',
                value: data.configured
                  ? new Date(data.configured.updatedAt).toLocaleString('ja-JP')
                  : '未設定（既定のまま）',
              },
            ]}
          />
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * サーバの応答を編集用の下書きへ落とす。
 *
 * **`configured` が null のときは `effective` を種にする。** 既定値を
 * このファイルで再定義すると、`orgPrivacyPolicySchema` の default が
 * 変わったときに画面だけ古い既定を出し続ける。
 */
export function toDraft(data: PrivacyPolicyView): OrgPrivacyPolicy {
  const src = data.configured ?? data.effective;
  return {
    remoteImageMode: src.remoteImageMode,
    egressMode: src.egressMode,
    prefetchEnabled: src.prefetchEnabled,
    stripTrackingPixels: src.stripTrackingPixels,
    stripReferrer: src.stripReferrer,
    cacheTtlSeconds: src.cacheTtlSeconds,
    bypassHosts: [...src.bypassHosts],
  };
}

export function splitHosts(value: string): string[] {
  return value
    .split('\n')
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s.length > 0)
    .slice(0, 50);
}

/* ------------------------------------------------------------ 取得元の実績 */

function HostStatsCard() {
  const [days, setDays] = React.useState(30);
  const stats = useQuery({
    queryKey: qk.imageProxyHosts(days),
    queryFn: () => api.get<ImageProxyHostStats>(`/privacy/image-proxy/hosts?days=${days}`),
  });

  type Row = ImageProxyHostStats['hosts'][number];
  const columns: Array<DataTableColumn<Row>> = [
    {
      id: 'host',
      header: '取得元',
      cell: (r) => <span className="break-all font-mono text-xs">{r.sourceHost}</span>,
    },
    {
      id: 'verdict',
      header: '判定',
      width: 'w-56',
      cell: (r) => (
        <Badge variant={r.verdict === 'clean' ? 'muted' : 'warning'} size="sm">
          {TRACKER_VERDICT_LABELS_JA[r.verdict] ?? r.verdict}
        </Badge>
      ),
    },
    {
      id: 'fetches',
      header: '取得回数',
      width: 'w-28',
      align: 'right',
      cell: (r) => <span className="tabular text-xs">{formatCount(r.fetches)}</span>,
    },
    {
      id: 'bytes',
      header: '通信量',
      width: 'w-28',
      align: 'right',
      hideBelow: 'md',
      cell: (r) => <span className="tabular text-xs">{formatBytes(r.bytes)}</span>,
    },
  ];

  return (
    <div className="space-y-4">
      <Field label="集計期間" className="max-w-[12rem]">
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">直近 7 日</SelectItem>
            <SelectItem value="30">直近 30 日</SelectItem>
            <SelectItem value="90">直近 90 日</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <DataTable
        columns={columns}
        rows={stats.data?.hosts ?? []}
        rowKey={(r) => `${r.sourceHost}-${r.verdict}`}
        loading={stats.isPending}
        error={stats.isError ? '取得元の集計を読み込めませんでした。' : undefined}
        emptyTitle="代理取得の実績がありません"
        emptyDescription="外部画像を含むメールがまだ届いていないか、読み込まない設定になっています。"
      />
    </div>
  );
}
